import { useMemo, useState } from 'react'

import { Writer } from '../../../Components'
import { JsonViewer } from '../../../Components/Common/JsonViewer'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { showToast } from '../../../utils/toast'
import { GigiChatMessages } from '../../Gigi/GigiForm/GigiChatMessages'
import { RequestSummary } from './RequestSummary'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Pannello chat + inspector JSON per la debug console.
 * Tre tab: Chat (messaggi conversazione), JSON (risposta completa Executor) e Metriche.
 * @param {Object} props
 * @param {Array} props.messages - Lista messaggi della conversazione
 * @param {Object|null} props.lastResponse - Ultima risposta ricevuta
 * @param {boolean} props.isLoading - Se è in corso un invio
 * @param {string|null} props.responseStatus - 'success', 'error' o null
 * @param {Function} props.sendMessage - Funzione invio messaggio
 * @param {string|undefined} props.conversationId - ID conversazione corrente
 * @param {Object} [props.config] - Configurazione corrente, per il riepilogo chip
 * @param {string[]} [props.configIssues] - Campi obbligatori mancanti nella config
 */
export function ResponseInspector({
  messages,
  lastResponse,
  isLoading,
  responseStatus,
  sendMessage,
  conversationId,
  config,
  configIssues = []
}) {
  const [activeTab, setActiveTab] = useState('chat')

  const statusModifier = isLoading ? 'loading' : responseStatus
  const statusLabel = isLoading
    ? t.dbcSending || 'Invio...'
    : responseStatus === 'success'
      ? t.dbcOk || 'OK'
      : responseStatus === 'error'
        ? t.errore || 'Errore'
        : null
  const statusIcon = isLoading
    ? 'pi-spin pi-spinner'
    : responseStatus === 'success'
      ? 'pi-check-circle'
      : 'pi-times-circle'

  const responseMetrics = useMemo(() => {
    if (!lastResponse?.data) return null
    const { data, elapsed } = lastResponse
    return {
      elapsed,
      tokenCount: data?.tokenCount || data?.chatGptInfo?.tokenCount,
      conversationId: data?.conversationData?.conversationId,
      messageToSend: data?.chatGptInfo?.messageToSend,
      processSteps: data?.processSteps?.length
    }
  }, [lastResponse])

  // Il tab Metriche esiste solo con una risposta a disposizione: dopo un reset
  // resterebbe selezionato un tab non più renderizzabile, lasciando il pannello vuoto.
  const currentTab = activeTab === 'metrics' && !responseMetrics ? 'chat' : activeTab

  const handleCopyJson = () => {
    try {
      const text = JSON.stringify(lastResponse?.data || lastResponse?.error, null, 2)
      navigator.clipboard.writeText(text)
      showToast(
        'success',
        t.dbcCopied || 'Copiato',
        t.dbcCopiedJson || 'JSON copiato negli appunti'
      )
    } catch (_) {
      showToast('error', t.errore || 'Errore', t.dbcCopyFailed || 'Impossibile copiare')
    }
  }

  return (
    <div className="ncard" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-comments" style={{ fontSize: 16 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className="ncard__title">{t.conversation || 'Conversazione'}</h3>
          <div className="ncard__sub">
            {messages.length} {t.messages || 'messaggi'}
            {conversationId && ` · ID: ${conversationId}`}
          </div>
        </div>
        {statusModifier && (
          <span className={`debug-status-badge debug-status-badge--${statusModifier}`}>
            <i className={`pi ${statusIcon}`} style={{ fontSize: 12 }} />
            {statusLabel}
            {responseMetrics?.elapsed && !isLoading ? ` (${responseMetrics.elapsed}ms)` : ''}
          </span>
        )}
        {conversationId && (
          <button
            className="debug-action-btn"
            onClick={() => window.open(`/operatore/${conversationId}`, '_blank', 'noopener')}
            title={t.dbcOpenConversation || 'Apri dettaglio conversazione'}
          >
            <i className="pi pi-external-link" style={{ fontSize: 12 }} />
            {t.dbcOpenConversation || 'Apri dettaglio conversazione'}
          </button>
        )}
      </div>

      {config && <RequestSummary config={config} />}

      {configIssues.length > 0 && (
        <div className="debug-inline-warning" role="status">
          <i className="pi pi-exclamation-triangle" />
          <span>
            {t.campiObbligatori || 'Campi mancanti'}: <strong>{configIssues.join(', ')}</strong>
          </span>
        </div>
      )}

      <div className="debug-tabs" role="tablist">
        <button
          id="debug-tab-chat"
          className={`debug-tab ${currentTab === 'chat' ? 'debug-tab--active' : ''}`}
          onClick={() => setActiveTab('chat')}
          role="tab"
          aria-selected={currentTab === 'chat'}
          aria-controls="debug-panel-chat"
        >
          <i className="pi pi-comments" style={{ fontSize: 13 }} />
          Chat
        </button>
        <button
          id="debug-tab-json"
          className={`debug-tab ${currentTab === 'json' ? 'debug-tab--active' : ''}`}
          onClick={() => setActiveTab('json')}
          role="tab"
          aria-selected={currentTab === 'json'}
          aria-controls="debug-panel-json"
        >
          <i className="pi pi-code" style={{ fontSize: 13 }} />
          JSON Response
          {lastResponse && <span className="debug-tab__dot" />}
        </button>
        {responseMetrics && (
          <button
            id="debug-tab-metrics"
            className={`debug-tab ${currentTab === 'metrics' ? 'debug-tab--active' : ''}`}
            onClick={() => setActiveTab('metrics')}
            role="tab"
            aria-selected={currentTab === 'metrics'}
            aria-controls="debug-panel-metrics"
          >
            <i className="pi pi-chart-bar" style={{ fontSize: 13 }} />
            Metriche
          </button>
        )}
      </div>

      <div
        className="ncard__body"
        style={{ padding: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        {currentTab === 'chat' && (
          <div
            id="debug-panel-chat"
            role="tabpanel"
            aria-labelledby="debug-tab-chat"
            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
          >
            <div className="chat-messages-area" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <GigiChatMessages messages={messages} isReadOnly={false} />
            </div>
            <div className="chat-writer-row">
              <Writer sendMessage={sendMessage} composerVariant="gigi" />
            </div>
          </div>
        )}

        {currentTab === 'json' && (
          <div
            id="debug-panel-json"
            role="tabpanel"
            aria-labelledby="debug-tab-json"
            style={{ flex: 1, overflow: 'auto', padding: 16 }}
          >
            {lastResponse ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12
                  }}
                >
                  <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                    {lastResponse.timestamp?.toLocaleTimeString()}
                  </span>
                  <button className="debug-action-btn" onClick={handleCopyJson}>
                    <i className="pi pi-copy" style={{ fontSize: 12 }} />
                    {t.dbcCopyJson || 'Copia JSON'}
                  </button>
                </div>
                {lastResponse.error ? (
                  <div className="debug-error-panel">
                    <strong>Error:</strong> {lastResponse.error}
                  </div>
                ) : (
                  <JsonViewer src={lastResponse.data} name="response" collapsed={1} />
                )}
                {lastResponse.payload && (
                  <div style={{ marginTop: 16 }}>
                    <label
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--ink-500)',
                        marginBottom: 6,
                        display: 'block'
                      }}
                    >
                      {t.dbcSentPayload || 'Payload Inviato'}
                    </label>
                    <JsonViewer src={lastResponse.payload} name="sentPayload" collapsed={2} />
                  </div>
                )}
              </>
            ) : (
              <div className="debug-empty-state">
                <i className="pi pi-inbox" style={{ fontSize: 32 }} />
                <span style={{ fontSize: 13 }}>
                  {t.dbcNoResponse || 'Nessuna risposta ancora. Invia un messaggio.'}
                </span>
              </div>
            )}
          </div>
        )}

        {currentTab === 'metrics' && responseMetrics && (
          <div
            id="debug-panel-metrics"
            role="tabpanel"
            aria-labelledby="debug-tab-metrics"
            style={{ flex: 1, overflow: 'auto', padding: 16 }}
          >
            <div className="debug-metrics-grid">
              <div className="debug-metric-card">
                <span className="debug-metric-card__label">
                  <i className="pi pi-clock" style={{ fontSize: 11 }} />
                  {t.dbcElapsed || 'Tempo risposta'}
                </span>
                <span className="debug-metric-card__value">{responseMetrics.elapsed}ms</span>
              </div>
              {responseMetrics.tokenCount != null && (
                <div className="debug-metric-card">
                  <span className="debug-metric-card__label">
                    <i className="pi pi-hashtag" style={{ fontSize: 11 }} />
                    {t.dbcTokens || 'Token'}
                  </span>
                  <span className="debug-metric-card__value">{responseMetrics.tokenCount}</span>
                </div>
              )}
              {responseMetrics.conversationId && (
                <div className="debug-metric-card">
                  <span className="debug-metric-card__label">
                    <i className="pi pi-comments" style={{ fontSize: 11 }} />
                    {t.dbcConvId || 'Conversation ID'}
                  </span>
                  <span className="debug-metric-card__value">{responseMetrics.conversationId}</span>
                </div>
              )}
              {responseMetrics.processSteps != null && (
                <div className="debug-metric-card">
                  <span className="debug-metric-card__label">
                    <i className="pi pi-list" style={{ fontSize: 11 }} />
                    {t.dbcSteps || 'Process Steps'}
                  </span>
                  <span className="debug-metric-card__value">{responseMetrics.processSteps}</span>
                </div>
              )}
            </div>
            {responseMetrics.messageToSend && (
              <div style={{ marginTop: 16 }}>
                <label
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--ink-500)',
                    marginBottom: 6,
                    display: 'block'
                  }}
                >
                  {t.dbcMessageToSend || 'Messaggio generato'}
                </label>
                <div
                  style={{
                    padding: 12,
                    background: 'var(--surface-2)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border)',
                    fontSize: 13,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {responseMetrics.messageToSend}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
